import axios from 'axios';
import { 
  LoginRequest, 
  LoginResponse, 
  User, 
  Publication, 
  CreatePublicationRequest,
  StageUpdateRequest,
  CoauthorRequestData,
  CoauthorResponseData,
  Grant
} from '../types';
import { cacheService } from './cache';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes timeout for long-running requests like PubMed refresh
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and check cache
api.interceptors.request.use(
  async (config) => {
    // Try to get token from Auth0 (if available) or localStorage
    let token = localStorage.getItem('token');
    
    // If no token in localStorage, try to get from Auth0
    // This will be handled by AuthContext which stores the token
    if (!token) {
      // Token should be set by AuthContext after Auth0 login
      token = localStorage.getItem('token');
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Check cache for GET requests to batch endpoints
    if (config.method === 'get' && config.url?.includes('/batch')) {
      const cacheKey = `${config.method}:${config.url}`;
      const cachedData = cacheService.get(cacheKey);
      if (cachedData) {
        // Return cached data as a resolved promise
        return Promise.reject({
          isCached: true,
          data: cachedData,
          config
        });
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors, track activity, and cache responses
api.interceptors.response.use(
  (response) => {
    // Reset inactivity timer on successful API calls
    // This will be handled by the AutoLogoutProvider when it detects the response

    // Cache successful batch endpoint responses
    if (response.config.method === 'get' && response.config.url?.includes('/batch')) {
      const cacheKey = `${response.config.method}:${response.config.url}`;
      cacheService.set(cacheKey, response.data, 2 * 60 * 1000); // Cache for 2 minutes
    }

    return response;
  },
  (error) => {
    // Handle cached data
    if (error.isCached) {
      return Promise.resolve({ data: error.data });
    }

    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data: LoginRequest): Promise<LoginResponse> =>
    api.post('/auth/login', data).then(res => res.data),
};

// Platform Stats API
export const platformAPI = {
  getPlatformStats: (): Promise<any> =>
    api.get('/platforms/stats').then(res => res.data),
};

// Access control API (for cross-system access like Data Finder / Source Data Discovery)
export const accessAPI = {
  canAccessDataFinder: (): Promise<{ allowed: boolean; reason?: string }> =>
    api.get('/access/data-finder').then(res => res.data),

  canAccessNonmemLicense: (): Promise<{ allowed: boolean; reason?: string }> =>
    api.get('/access/licenses/nonmem').then(res => res.data),
};

// Licenses API
export const licensesAPI = {
  listLicenses: (): Promise<any[]> =>
    api.get('/licenses').then(res => res.data),
  
  createLicense: (data: { code: string; display_name: string; expires_on: string; description?: string | null; is_active: boolean }): Promise<{ message: string; licenseId: number }> =>
    api.post('/licenses', data).then(res => res.data),
  
  uploadLicenseFile: (licenseId: number, file: File): Promise<{ message: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/licenses/${licenseId}/file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  },
  
  downloadLicense: async (id: number): Promise<Blob> => {
    const response = await api.get(`/licenses/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Access Control API (for managing user access permissions)
export const accessControlAPI = {
  getUsers: (): Promise<any[]> =>
    api.get('/access-control/users').then(res => res.data),
  
  getUserAccessControl: (userId: number): Promise<{ data_discovery_access?: 'Y' | 'N' | 'C' | null; license_access?: 'Y' | 'N' | 'C' | null }> =>
    api.get(`/access-control/users/${userId}`).then(res => res.data),
  
  updateUserAccess: (userId: number, data: { data_discovery_access?: 'Y' | 'N' | 'C'; license_access?: 'Y' | 'N' | 'C' }): Promise<{ message: string }> =>
    api.put(`/access-control/users/${userId}`, data).then(res => res.data),
};

// User API
export const userAPI = {
  getCurrentUser: (): Promise<User> =>
    api.get('/users/me').then(res => res.data),
  
  getUsers: (): Promise<User[]> =>
    api.get('/users').then(res => res.data),
  searchUsers: (q: string): Promise<Array<{ id: number; first_name: string; last_name: string; email: string }>> =>
    api.get(`/users/search`, { params: { q } }).then(res => res.data),
  
  updateUserRole: (id: number, role: string): Promise<void> =>
    api.put(`/users/${id}/role`, { role }).then(res => res.data),

  updateProfile: (data: any): Promise<User> =>
    api.put('/users/profile', data).then(res => res.data),

  deactivateUser: (id: number): Promise<{ message: string }> =>
    api.put(`/users/${id}/deactivate`).then(res => res.data),
  
  activateUser: (id: number): Promise<{ message: string }> =>
    api.put(`/users/${id}/activate`).then(res => res.data),
};

// Publication API
export const publicationAPI = {
  getPublications: (stage?: string, search?: string): Promise<Publication[]> => {
    const params = new URLSearchParams();
    if (stage) params.append('stage', stage);
    if (search) params.append('search', search);
    
    return api.get(`/publications?${params.toString()}`).then(res => res.data);
  },

  getPublication: (id: number): Promise<Publication> =>
    api.get(`/publications/${id}`).then(res => res.data),

  createPublication: (data: CreatePublicationRequest): Promise<{ message: string; publicationId: number }> =>
    api.post('/publications', data).then(res => res.data),

  approvePublication: (id: number, notes?: string): Promise<{ message: string }> =>
    api.put(`/publications/${id}/approve`, { notes }).then(res => res.data),

  rejectPublication: (id: number, reason: string): Promise<{ message: string }> =>
    api.put(`/publications/${id}/reject`, { reason }).then(res => res.data),

  resubmitPublication: (id: number, feedback: string): Promise<{ message: string }> =>
    api.put(`/publications/${id}/resubmit`, { feedback }).then(res => res.data),

  resubmitForReview: (id: number): Promise<{ message: string }> =>
    api.put(`/publications/${id}/resubmit-for-review`).then(res => res.data),
  getEditHistory: (id: number): Promise<any[]> =>
    api.get(`/publications/${id}/edit-history`).then(res => res.data),
  getResubmissionFeedback: (id: number): Promise<{ feedback: string | null; hasFeedback: boolean }> =>
    api.get(`/publications/${id}/resubmission-feedback`).then(res => res.data),

  updateStage: (id: number, data: StageUpdateRequest): Promise<{ message: string }> =>
    api.put(`/publications/${id}/stage`, data).then(res => res.data),

  updatePublicationStage: (id: number, data: StageUpdateRequest): Promise<{ message: string }> =>
    api.put(`/publications/${id}/stage`, data).then(res => res.data),
  
  markAsPublished: (id: number, data: FormData): Promise<{ message: string }> =>
    api.post(`/publications/${id}/mark-published`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(response => response.data),

  updatePublication: (id: number, data: { title?: string; abstract?: string; methods?: string; dataSources?: number[] }): Promise<{ message: string }> =>
    api.put(`/publications/${id}`, data).then(res => res.data),

  updatePriority: (id: number, data: { priorityLevel: 'normal' | 'priority'; followUpDate?: string }): Promise<{ message: string }> =>
    api.put(`/publications/${id}/priority`, data).then(res => res.data),

  changeLeadAuthor: (id: number, newLeadAuthorId: number): Promise<{ message: string; oldLeadAuthorId: number; newLeadAuthorId: number; newLeadAuthorName: string }> =>
    api.put(`/publications/${id}/lead-author`, { newLeadAuthorId }).then(res => res.data),

  getPublicationTeams: (id: number): Promise<any[]> =>
    api.get(`/publications/${id}/teams`).then(res => res.data),

  setPublicationTeams: (id: number, data: { team_ids: number[] }): Promise<{ message: string }> =>
    api.put(`/publications/${id}/teams`, data).then(res => res.data),
};

// Co-author API
export const coauthorAPI = {
  getCoauthorRequests: (publicationId: number): Promise<any[]> =>
    api.get(`/publications/${publicationId}/coauthor-requests`).then(res => res.data),

  requestCoauthor: (publicationId: number, data: CoauthorRequestData): Promise<{ message: string }> =>
    api.post(`/publications/${publicationId}/coauthor-request`, data).then(res => res.data),

  respondToRequest: (requestId: number, data: CoauthorResponseData): Promise<{ message: string }> =>
    api.put(`/coauthor-requests/${requestId}/respond`, data).then(res => res.data),
  addCoauthor: (publicationId: number, payload: { userId?: number; name?: string; email?: string }): Promise<{ message: string }> =>
    api.post(`/publications/${publicationId}/add-coauthor`, payload).then(res => res.data),
};

// Data Sources API
export const dataSourcesAPI = {
  getDataSources: (): Promise<any[]> =>
    api.get('/data-sources').then(res => res.data),

  getAllDataSources: (): Promise<any[]> =>
    api.get('/data-sources/all').then(res => res.data),

  createDataSource: (data: { name: string; description?: string; is_active?: boolean }): Promise<{ message: string; id: number }> =>
    api.post('/data-sources', data).then(res => res.data),

  updateDataSource: (id: number, data: { name?: string; description?: string; is_active?: boolean }): Promise<{ message: string }> =>
    api.put(`/data-sources/${id}`, data).then(res => res.data),

  deleteDataSource: (id: number): Promise<{ message: string }> =>
    api.delete(`/data-sources/${id}`).then(res => res.data),
};

// Grant API
export const grantsAPI = {
  getGrants: (): Promise<Grant[]> => api.get('/grants').then(response => response.data),
  getGrantPublications: (id: number): Promise<any[]> => api.get(`/grants/${id}/publications`).then(response => response.data),
  refreshGrantPublications: (id: number): Promise<{ message: string; publications_found: number; new_publications_added: number }> => 
    api.post(`/grants/${id}/refresh`).then(response => response.data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (): Promise<{
    publications: {
      total: number;
      inProgress: number;
      published: number;
      stage1: number;
      stage2: number;
      stage3: number;
      stage4: number;
    };
    users: {
      total: number;
      active: number;
      inactive: number;
    };
  }> => api.get('/dashboard/stats').then(response => response.data),
};

// Users API (for Active Users dashboard)
export const usersAPI = {
  getAllUsers: (): Promise<User[]> => api.get('/users/all').then(response => response.data),
};

// Public API instance (no authentication)
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Export API
export const exportAPI = {
  downloadManuscriptTracker: (): Promise<Blob> => 
    publicApi.get('/export/manuscript-tracker', {
      responseType: 'blob',
    }).then(response => response.data),
};

// Meeting API
export const meetingAPI = {
  createMeeting: (data: FormData): Promise<{ message: string; meetingId: number; filesUploaded: number }> => 
    api.post('/meetings', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(response => response.data),
  
  getMeetings: (publicationId: number): Promise<any[]> => 
    api.get(`/publications/${publicationId}/meetings`).then(response => response.data),
  
  downloadFile: (meetingId: number, fileId: number): Promise<Blob> => 
    api.get(`/meetings/${meetingId}/files/${fileId}`, {
      responseType: 'blob',
    }).then(response => response.data),

  updateMeetingNotes: (meetingId: number, notes: string): Promise<{ message: string }> =>
    api.put(`/meetings/${meetingId}/notes`, { notes }).then(response => response.data),
};

// Timeline API
export const timelineAPI = {
  getTimeline: (publicationId: number): Promise<any[]> => 
    api.get(`/publications/${publicationId}/timeline`).then(response => response.data),
  
  createTimelineItem: (publicationId: number, data: any): Promise<{ message: string; timelineId: number }> => 
    api.post(`/publications/${publicationId}/timeline`, data).then(response => response.data),
  
  updateTimelineItem: (timelineId: number, data: any): Promise<{ message: string }> => 
    api.put(`/timeline/${timelineId}`, data).then(response => response.data),
  
  deleteTimelineItem: (timelineId: number): Promise<{ message: string }> => 
    api.delete(`/timeline/${timelineId}`).then(response => response.data),
};

// Objectives API
export const objectivesAPI = {
  getObjectives: (publicationId: number): Promise<any[]> => 
    api.get(`/publications/${publicationId}/objectives`).then(response => response.data),
  
  createObjective: (publicationId: number, data: any): Promise<{ message: string; objectiveId: number }> => 
    api.post(`/publications/${publicationId}/objectives`, data).then(response => response.data),
  
  updateObjective: (objectiveId: number, data: any): Promise<{ message: string }> => 
    api.put(`/objectives/${objectiveId}`, data).then(response => response.data),
  
  deleteObjective: (objectiveId: number): Promise<{ message: string }> => 
    api.delete(`/objectives/${objectiveId}`).then(response => response.data),
  
  uploadAttachment: (objectiveId: number, file: File): Promise<{ message: string; attachmentId: number }> => {
    const formData = new FormData();
    formData.append('attachment', file);
    return api.post(`/objectives/${objectiveId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(response => response.data);
  },
  
  downloadAttachment: (objectiveId: number, attachmentId: number): Promise<Blob> => 
    api.get(`/objectives/${objectiveId}/attachments/${attachmentId}`, {
      responseType: 'blob',
    }).then(response => response.data),
  
  deleteAttachment: (objectiveId: number, attachmentId: number): Promise<{ message: string }> =>
    api.delete(`/objectives/${objectiveId}/attachments/${attachmentId}`).then(response => response.data),
};

export const timelineNotificationAPI = {
  sendNotification: (timelineId: number, data: { subject: string; body: string; recipientEmail: string }): Promise<{ message: string }> =>
    api.post(`/timeline/${timelineId}/send-notification`, data).then(response => response.data),
};

export const teamAPI = {
  getTeams: (): Promise<any[]> => 
    api.get('/teams').then(response => response.data),
  
  createTeam: (data: any): Promise<{ message: string; teamId: number }> => 
    api.post('/teams', data).then(response => response.data),
  
  updateTeam: (teamId: number, data: any): Promise<{ message: string }> => 
    api.put(`/teams/${teamId}`, data).then(response => response.data),
  
  deleteTeam: (teamId: number): Promise<{ message: string }> => 
    api.delete(`/teams/${teamId}`).then(response => response.data),
  
  getTeamMembers: (teamId: number): Promise<any[]> => 
    api.get(`/teams/${teamId}/members`).then(response => response.data),
  
  addTeamMember: (teamId: number, data: any): Promise<{ message: string }> => 
    api.post(`/teams/${teamId}/members`, data).then(response => response.data),
  
  updateTeamMember: (teamId: number, memberId: number, data: any): Promise<{ message: string }> => 
    api.put(`/teams/${teamId}/members/${memberId}`, data).then(response => response.data),
  
  removeTeamMember: (teamId: number, memberId: number): Promise<{ message: string }> => 
    api.delete(`/teams/${teamId}/members/${memberId}`).then(response => response.data),
  
  getTeamPublications: (teamId: number): Promise<any[]> => 
    api.get(`/teams/${teamId}/publications`).then(response => response.data),
};

// Data Mapping API
export const dataMappingAPI = {
  getDataSources: (): Promise<any[]> =>
    api.get('/data-mapping/data-sources').then(response => response.data),

  getMedications: (): Promise<any[]> =>
    api.get('/data-mapping/medications').then(response => response.data),

  getRegimens: (): Promise<any[]> =>
    api.get('/data-mapping/regimens').then(response => response.data),

  createDataSourceMapping: (data: any): Promise<{ message: string; id?: number }> =>
    api.post('/data-mapping/data-sources', data).then(response => response.data),

  createMedicationMapping: (data: any): Promise<{ message: string; id?: number }> =>
    api.post('/data-mapping/medications', data).then(response => response.data),

  createRegimenMapping: (data: any): Promise<{ message: string; id?: number }> =>
    api.post('/data-mapping/regimens', data).then(response => response.data),
};

// Polls API
export const pollsAPI = {
  getPolls: (filters?: { created_by_id?: number; status?: string }): Promise<any[]> =>
    api.get('/polls', { params: filters }).then(response => response.data),

  getPollsForUser: (): Promise<any[]> =>
    api.get('/polls/for-user').then(response => response.data),

  getPoll: (id: number): Promise<any> =>
    api.get(`/polls/${id}`).then(response => response.data),

  createPoll: (data: any): Promise<{ id: number; message: string }> =>
    api.post('/polls', data).then(response => response.data),

  updatePoll: (id: number, data: any): Promise<{ message: string }> =>
    api.put(`/polls/${id}`, data).then(response => response.data),

  submitResponse: (pollId: number, responseData: any): Promise<{ message: string }> =>
    api.post(`/polls/${pollId}/respond`, { response_data: responseData }).then(response => response.data),

  getNotificationCount: (): Promise<{ count: number }> =>
    api.get('/polls/notifications/count').then(response => response.data),

  getNotifications: (): Promise<any[]> =>
    api.get('/polls/notifications').then(response => response.data),

  sendReminders: (pollId: number): Promise<{ success: boolean; remindersSent: number; message: string }> =>
    api.post(`/polls/${pollId}/send-reminders`).then(response => response.data),
};

export default api;
